import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';
import { PUR2020_R01Dto } from './Dto_PUR2020_R01';
import { getCurrentMonthDateRange } from 'src/utils/date/dateUtils';

export interface PUR2020_R01Filter {
    compCd: string;
    customCd: string;
    customNm: string;
    transportDt: DateRange | undefined;
    workplaceCd: string;
    logicCenter: string;
    itemCd: string;
    itemNm: string;
    brandCd: string;
    inoutClass: string;
    itemGroup: string;
}

// (소계 제거로 확장 타입 불필요)

/**
 * Zustand 스토어의 상태 및 액션 정의
 */
export interface Store_PUR2020_R01 {
    PUR2020_R01Filter: PUR2020_R01Filter;
    PUR2020_R01PastFilter: PUR2020_R01Filter;
    setPUR2020_R01Filter: (filter: Partial<PUR2020_R01Filter>) => void;
    setPUR2020_R01PastFilter: (filter: Partial<PUR2020_R01Filter>) => void;

    // === 리스트 관련 ===
    PUR2020_R01List: PUR2020_R01Dto[];
    setPUR2020_R01List: (PUR2020_R01List: PUR2020_R01Dto[]) => void;
    insertPUR2020_R01List: (data: PUR2020_R01Dto[], index?: number) => void;
    removePUR2020_R01List: (index: number) => void;
    updatePUR2020_R01List: (data: PUR2020_R01Dto, index: number) => void;

    // === 선택 관련 ===
    selectedPUR2020_R01List: string[];
    setSelectedPUR2020_R01List: (idList: string[]) => void;
    addSelectedPUR2020_R01List: (idList: string[]) => void;
    removeSelectedPUR2020_R01List: (idList?: string[]) => void;
    initSelectedPUR2020_R01List: () => void;

    // === AG Grid 컬럼 상태 관리 ===
    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;

    allCount: number;
    setAllCount: (allCount: number) => void;

    scrollTop: number | null;
    setScrollTop: (top: number | null) => void;

    // (소계 제거) 추가 상태 없음

    // === 전체 초기화 ===
    initData: () => void;
}

/**
 * 기본 날짜 범위 (현재 월)
 */
const defaultDateRange: DateRange = getCurrentMonthDateRange();

export const defaultPUR2020_R01Filter: PUR2020_R01Filter = {
    compCd: '',
    inoutClass: '',
    customCd: '',
    customNm: '',
    transportDt: defaultDateRange,
    workplaceCd: '',
    logicCenter: '',
    itemCd: '',
    itemNm: '',
    brandCd: '',
    itemGroup: '',
};

/**
 * Zustand 스토어 정의
 */
export const usePUR2020_R01Store = create<Store_PUR2020_R01>((set) => {
    /**
     * 공통 필터 설정 함수 생성
     */
    const createFilterSetter = (filterKey: keyof Pick<Store_PUR2020_R01, 'PUR2020_R01Filter' | 'PUR2020_R01PastFilter'>) => 
        (filter: Partial<PUR2020_R01Filter>) => set((state) => ({
            ...state,
            [filterKey]: { ...state[filterKey], ...filter }
        }));

    /** 
     * 선택된 항목 제거 헬퍼 함수
     */
    const removeItemsFromArray = <T>(array: T[], itemsToRemove: T[]): T[] => {
        return array.filter(item => !itemsToRemove.includes(item));
    };

    return {
        PUR2020_R01Filter: defaultPUR2020_R01Filter,
        PUR2020_R01PastFilter: defaultPUR2020_R01Filter,
        setPUR2020_R01Filter: createFilterSetter('PUR2020_R01Filter'),
        setPUR2020_R01PastFilter: createFilterSetter('PUR2020_R01PastFilter'),

        // === 리스트 관련 ===
        PUR2020_R01List: [],
        setPUR2020_R01List: (PUR2020_R01List: PUR2020_R01Dto[]) => set({ PUR2020_R01List }),
        insertPUR2020_R01List: (data: PUR2020_R01Dto[], index?: number) => set((state) => {
            const _PUR2020_R01List = [...state.PUR2020_R01List];
            
            if (index !== undefined) {
                _PUR2020_R01List.splice(index, 0, ...data);
            } else {
                _PUR2020_R01List.push(...data);
            }
            return { ...state, PUR2020_R01List: _PUR2020_R01List };
        }),
        removePUR2020_R01List: (deletedIndex: number) => set((state) => ({
            ...state,
            PUR2020_R01List: state.PUR2020_R01List.filter((_, index) => index !== deletedIndex),
        })),
        updatePUR2020_R01List: (data: PUR2020_R01Dto, index: number) => set((state) => {
            const _PUR2020_R01List = [...state.PUR2020_R01List];
            _PUR2020_R01List[index] = data;
            return { ...state, PUR2020_R01List: _PUR2020_R01List };
        })
        ,

        // === 선택 관련 ===
        selectedPUR2020_R01List: [],
        setSelectedPUR2020_R01List: (idList: string[]) => set({ selectedPUR2020_R01List: idList }),
        addSelectedPUR2020_R01List: (idList: string[]) => set((state) => {
            const _selectedPUR2020_R01List = [...state.selectedPUR2020_R01List];
            _selectedPUR2020_R01List.push(...idList.filter(value => !_selectedPUR2020_R01List.includes(value)));
            return { ...state, selectedPUR2020_R01List: _selectedPUR2020_R01List };
        }),
        removeSelectedPUR2020_R01List: (idList?: string[]) => set((state) => {
            if (!idList) {
                return { ...state, selectedPUR2020_R01List: [] };
            }
            
            return {
                ...state,
                selectedPUR2020_R01List: removeItemsFromArray(state.selectedPUR2020_R01List, idList),
            };
        }),
        initSelectedPUR2020_R01List: () => set({ selectedPUR2020_R01List: [] }),
        
        // === AG Grid 컬럼 상태 관리 ===
        columnState: null,
        setColumnState: (state) => set({ columnState: state }),

        allCount: 0,
		
        setAllCount: (allCount: number) => set({ allCount }),

        scrollTop: null,
        setScrollTop: (top: number | null) => set({ scrollTop: top }),

        // === 통합 소계 관련 ===
        

        // === 전체 초기화 ===
        initData: () => set(() => ({
            PUR2020_R01Filter: defaultPUR2020_R01Filter,
            PUR2020_R01PastFilter: defaultPUR2020_R01Filter,
            PUR2020_R01List: [],
            selectedPUR2020_R01List: [],
            columnState: null,
            allCount: 0,
            scrollTop: null,
		
            
        })),
    };
});


